import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, UserCog, Dumbbell, Activity, Shield, Clock, ClipboardCheck, ChevronRight } from "lucide-react";

const asList = (d) => (Array.isArray(d) ? d : d?.users || d?.items || d?.patients || d?.results || []);

const Tile = ({ icon: Icon, value, label, bg, iconColor, testId }) => (
  <div className={`rounded-2xl p-5 ${bg}`} data-testid={testId}>
    <Icon className="h-6 w-6" style={{ color: iconColor }} />
    <p className="mt-3 text-3xl font-bold text-[#1f3a5f]">{value}</p>
    <p className="text-sm text-[#1f3a5f]/70">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(0);
  const [braceRows, setBraceRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [s, p, u, pres] = await Promise.allSettled([
        api.adminStats(), api.pendingContent(), api.adminUsers(), api.allBracePrescriptions(),
      ]);
      if (s.status === "fulfilled") setData(s.value); else throw s.reason;
      if (p.status === "fulfilled") {
        const pc = p.value;
        setPending(pc?.total ?? ((pc?.exercises?.length || 0) + (pc?.articles?.length || 0)));
      }
      const users = u.status === "fulfilled" ? asList(u.value) : [];
      const prescMap = {};
      if (pres.status === "fulfilled") (pres.value?.items || []).forEach((x) => { prescMap[x.patient_id] = x.prescribed_hours; });
      const patients = users.filter((x) => x.role === "paziente");
      const detailed = await Promise.all(patients.map(async (pt) => {
        const [pd, bl] = await Promise.allSettled([api.patient(pt.id), api.braceLogs(pt.id)]);
        const wears = pd.status === "fulfilled" ? !!pd.value?.profile?.wears_brace : false;
        const summary = bl.status === "fulfilled" ? bl.value?.summary : null;
        return { id: pt.id, name: pt.full_name || pt.username, wears, avg: summary?.avg_hours ?? null, prescribed: prescMap[pt.id] ?? null };
      }));
      setBraceRows(detailed.filter((r) => r.wears));
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const d = data || {};
  const adherence = typeof d.avg_adherence === "number" ? `${Math.round(d.avg_adherence)}%` : "—";

  return (
    <div>
      <PageHeader title="Pannello admin" subtitle="Amministrazione della piattaforma Science2Movement." />

      <button
        onClick={() => navigate("/admin/content")}
        className="mb-8 flex w-full items-center gap-4 rounded-2xl bg-[#5b87c9] p-6 text-left text-white transition-all hover:brightness-105"
        data-testid="pending-banner"
      >
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20"><ClipboardCheck className="h-6 w-6" /></div>
        <div className="flex-1">
          <p className="text-xl font-bold">{pending} contenut{pending === 1 ? "o" : "i"} da approvare</p>
          <p className="text-sm text-white/85">Nuovi esercizi/articoli dei fisioterapisti attendono la tua decisione.</p>
        </div>
        <ChevronRight className="h-6 w-6" />
      </button>

      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Panoramica sistema</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Tile icon={Users} value={d.total_patients ?? "—"} label="Pazienti" bg="bg-[#e6f0e9]" iconColor="#5b87c9" testId="admin-stat-patients" />
        <Tile icon={UserCog} value={d.total_physios ?? "—"} label="Fisioterapisti" bg="bg-[#fbe9db]" iconColor="#5f9c96" testId="admin-stat-physios" />
        <Tile icon={Dumbbell} value={d.total_exercises ?? "—"} label="Esercizi" bg="bg-[#e6f0e9]" iconColor="#5b87c9" testId="admin-stat-exercises" />
        <Tile icon={Activity} value={adherence} label="Aderenza media" bg="bg-[#e6f0e9]" iconColor="#5f9c96" testId="admin-stat-adherence" />
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Utenti totali: <b className="text-[#1f3a5f]">{d.total_users ?? "—"}</b> &nbsp; Articoli: <b className="text-[#1f3a5f]">{d.total_articles ?? "—"}</b> &nbsp; Admin: <b className="text-[#1f3a5f]">{d.total_admins ?? "—"}</b>
      </p>

      <p className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monitoraggio busto</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Tile icon={Shield} value={d.brace_patients ?? "—"} label="Pazienti con busto" bg="bg-[#fbe9db]" iconColor="#5f9c96" testId="admin-brace-patients" />
        <Tile icon={Clock} value={d.brace_avg_hours != null ? `${d.brace_avg_hours}h` : "—"} label="Ore busto medie/die" bg="bg-[#fbe9db]" iconColor="#5f9c96" testId="admin-brace-avg" />
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Ore busto totali registrate: <b className="text-[#1f3a5f]">{d.brace_total_hours != null ? `${d.brace_total_hours}h` : "—"}</b>
      </p>

      <p className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Report pazienti portatori di busto</p>
      {braceRows.length === 0 ? (
        <EmptyState title="Nessun portatore di busto" subtitle="Nessun paziente è attualmente segnato come portatore di busto." />
      ) : (
        <Card className="border-border/70">
          <CardContent className="p-0">
            <Table data-testid="admin-brace-report">
              <TableHeader>
                <TableRow>
                  <TableHead>Paziente</TableHead>
                  <TableHead className="text-right">Media indossate</TableHead>
                  <TableHead className="text-right">Prescritte</TableHead>
                  <TableHead className="text-right">Aderenza</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {braceRows.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-brace-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-right">{r.avg != null ? `${r.avg}h` : "—"}</TableCell>
                    <TableCell className="text-right">{r.prescribed != null ? `${r.prescribed}h` : "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-[#1f3a5f]">{r.prescribed && r.avg != null ? `${Math.round((r.avg / r.prescribed) * 100)}%` : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
